import {
    Boxscore,
    Game,
    getBoxscoreApiV1BoxscoreGameIdGet,
    getClipApiV1ClipGet,
    getNbaGamePbpApiV1NbaGamesGameIdPbpGet,
    GetNbaGamePbpApiV1NbaGamesGameIdPbpGetResponse,
    getNbaGamesApiV1NbaGamesGet,
    getWnbaGamePbpApiV1WnbaGamesGameIdPbpGet,
    getWnbaGamesApiV1WnbaGamesGet,
    getNbaGamesPbpBatchApiV1NbaGamesPbpBatchPost,
    getWnbaGamesPbpBatchApiV1WnbaGamesPbpBatchPost,
    Player,
    Season,
    SeasonType,
    Stat,
    VideoEvent,
    GameSubQuery,
    BatchGamePbpRequest,
    GetNbaGamesPbpBatchApiV1NbaGamesPbpBatchPostResponse,
    GetWnbaGamesPbpBatchApiV1WnbaGamesPbpBatchPostResponse
} from "../client";
import { LEAGUE } from "../models/NBA";
import { showErrorNotification } from "../utils/errors";

export const getGames = async (league: LEAGUE, season: Season, seasonType: SeasonType, dateFrom: Date | undefined, dateTo: Date | undefined): Promise<Game[]> => {
    if (!season || String(season) === 'null' || String(season) === 'undefined') {
        return []
    }

    try {
        const res = league === LEAGUE.NBA ?
            await getNbaGamesApiV1NbaGamesGet({
                query: { season, seasonType, dateFrom, dateTo }
            }) :
            await getWnbaGamesApiV1WnbaGamesGet({
                query: { season, seasonType, dateFrom, dateTo }
            })

        if (res.error !== undefined) throw new Error('Error getting games')
        const data = res.data as Game[]
        return data.map((game: Game) => ({...game, date: new Date(game.date)}))

    } catch (error) {
        showErrorNotification({
            id: 'league-data-fetch-error',
            title: 'Unable to load games',
            message: `Could not retrieve ${league} games. Please try again later.`,
            error
        });
        return []
    }
}

export const getClip = async (gameId: string, eventNumber: string): Promise<VideoEvent | null> => {
    try {
        if (gameId.length === 8) {
            gameId = "00" + gameId
        }
        const res = await getClipApiV1ClipGet({
            query: { gameId, eventNumber}
        })
        if (res.error !== undefined) throw new Error('Error get PBP clip for gameId: ' + gameId + ' eventNumber: ' + eventNumber)
        const data = res.data as VideoEvent
        return data

    } catch (error) {
        showErrorNotification({
            id: 'video-clip-error',
            title: 'Unable to load video',
            message: 'Could not load the requested video clip. Please try again later.',
            error
        });
        return null
    }
}

export const getBoxscore = async (gameId: string): Promise<Boxscore | null> => {
    try {
        if (gameId.length === 8) {
            gameId = "00" + gameId
        }
        const res = await getBoxscoreApiV1BoxscoreGameIdGet({
            path: { game_id: gameId }
        })
        if (res.error !== undefined) throw new Error('Error getting Boxscore for gameId: ' + gameId)
        const boxscore = res.data as Boxscore
        return boxscore

    } catch (error) {
        showErrorNotification({
            id: 'boxscore-fetch-error',
            title: 'Unable to load box score',
            message: 'Could not load box score statistics for this game.',
            error
        });
        return null
    }
}

export const getGameActivePlayers = async (
    gameId: string,
    knownPlayers: Player[] = []
): Promise<Player[]> => {
    const boxscore = await getBoxscore(gameId);
    if (!boxscore) return [];

    const rawBoxPlayers = [
        ...(boxscore.homeTeam?.players || []),
        ...(boxscore.awayTeam?.players || []),
    ].filter((p) => p.played === "1" || p.played === "true");

    return rawBoxPlayers.map((bp) => {
        const matched = knownPlayers.find((p) => String(p.id) === String(bp.personId));
        if (matched) return matched;
        return {
            id: String(bp.personId),
            fullName: bp.name,
            firstName: bp.firstName || bp.name.split(" ")[0] || "",
            lastName: bp.familyName || bp.name.split(" ").slice(1).join(" ") || "",
            isActive: true,
        };
    });
};

export const getGamePBPVideos = async (
    gameId: string,
    league: LEAGUE,
    seasonType: SeasonType,
    categories: Stat[],
    cursor: string | null,
    limit: number,
    periods: number[],
    playerIds: string[] | undefined,
    order: 'asc' | 'desc',
    signal?: AbortSignal
): Promise<GetNbaGamePbpApiV1NbaGamesGameIdPbpGetResponse | null> => {
    try {
        const options = {
            query: {
                contexts: categories,
                seasonType,
                cursor,
                limit,
                ...(playerIds && playerIds.length > 0 ? { players: playerIds } : {}),
                periods,
                order,
            },
            path: { game_id: gameId },
            signal
        }
        const res = league === LEAGUE.NBA ?
            await getNbaGamePbpApiV1NbaGamesGameIdPbpGet(options) :
            await getWnbaGamePbpApiV1WnbaGamesGameIdPbpGet(options)
        if (res.error !== undefined) throw new Error('Error getting PBP video data for ' + gameId)
        const data = res.data as GetNbaGamePbpApiV1NbaGamesGameIdPbpGetResponse
        return data

    } catch (error) {
        showErrorNotification({
            id: 'search-results-error',
            title: 'Unable to load game clips',
            message: 'Could not retrieve play-by-play video clips. Please try again.',
            error
        });
        return null
    }
}

export const getBatchGamePBPVideos = async (
    league: LEAGUE,
    queries: GameSubQuery[],
    cursor: string | null,
    limit: number,
    order: 'asc' | 'desc',
    signal?: AbortSignal
): Promise<GetNbaGamesPbpBatchApiV1NbaGamesPbpBatchPostResponse | GetWnbaGamesPbpBatchApiV1WnbaGamesPbpBatchPostResponse | null> => {
    try {
        const body: BatchGamePbpRequest = {
            queries,
            cursor,
            limit,
            order,
        }
        const options = {
            body,
            signal
        }
        const res = league === LEAGUE.NBA
            ? await getNbaGamesPbpBatchApiV1NbaGamesPbpBatchPost(options)
            : await getWnbaGamesPbpBatchApiV1WnbaGamesPbpBatchPost(options)

        if (res.error !== undefined) throw new Error('Error getting batch game video data')
        return res.data
    } catch (error) {
        showErrorNotification({
            id: 'search-results-error',
            title: 'Unable to load game clips',
            message: 'Could not retrieve video highlights for stacked games. Please try again.',
            error
        });
        return null
    }
}